import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../auth/AuthContext';
import TermSummarySheet from './TermSummarySheet';
import TermWorksheet from './TermWorksheet';

/*
  SubmissionsCard

  The Submissions tab body. Shows every lender this deal was sent to and where
  each one stands: sent/waiting, term sheet received, declined, followed up,
  expired. A waiting lender's row has an "Upload term sheet" control: picking a
  file uploads it to the deal's Drive folder, files it into Documents (tagged
  Term Sheet, category L), links it to that lender's submission, flips the row
  to "Term sheet received", and auto-runs the AI extraction. The extracted
  terms then show inline on the row.

  Reads from list-lender-submissions. Upload reuses the same Drive pipeline as
  DocumentsCard (get-upload-url -> Drive PUT -> confirm-upload), passing the
  submissionId so confirm-upload performs the two-way link.

  Props:
    deal           the deal record (deal.id, Drive folder ID in fields)
    lenders        lender list (from list-lenders) for name lookup
    onOpenCompare  optional callback when "Compare and send" is clicked
    collapsed, onToggle  card collapse plumbing (matches DocumentsCard)
*/

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'docx', 'xlsx'];
const MAX_FILE_SIZE = 50 * 1024 * 1024;
const TERM_SHEET_DOC_TYPE = 'Term Sheet';

export default function SubmissionsCard({ deal, lenders, collapsed, onToggle, onOpenCompare }) {
  const dealId = deal?.id;
  const driveFolderId = deal?.fields?.['Drive folder ID'] || '';
  const safeLenders = Array.isArray(lenders) ? lenders : [];
  const lenderById = {};
  safeLenders.forEach((l) => { if (l && l.id) lenderById[l.id] = l; });

  const [submissions, setSubmissions] = useState([]);
  const [summary, setSummary] = useState({ sent: 0, termSheets: 0, waiting: 0, declined: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Per-submission transient UI: { phase: 'uploading'|'reading'|'error', pct, msg }
  const [rowState, setRowState] = useState({});
  // View terms modal: holds the submission whose term sheet is open, or null.
  const [viewing, setViewing] = useState(null);
  // Comparison modal open flag.
  const [comparing, setComparing] = useState(false);
  // Cash to close worksheet: holds { sub, lenderName } or null.
  const [worksheetFor, setWorksheetFor] = useState(null);

  const patchRow = (id, patch) => setRowState((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));
  const clearRow = (id) => setRowState((p) => { const n = { ...p }; delete n[id]; return n; });

  const refresh = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`/.netlify/functions/list-lender-submissions?dealId=${dealId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load submissions');
      setSubmissions(data.submissions || []);
      setSummary(data.summary || { sent: 0, termSheets: 0, waiting: 0, declined: 0 });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { refresh(); }, [refresh]);

  const uploadTermSheet = useCallback(async (sub, file, lenderName) => {
    if (!driveFolderId) {
      patchRow(sub.id, { phase: 'error', msg: 'This deal has no Drive folder yet.' });
      return;
    }
    const ext = fileExtension(file.name);
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      patchRow(sub.id, { phase: 'error', msg: `File type .${ext} not allowed.` });
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      patchRow(sub.id, { phase: 'error', msg: 'File too large (max 50MB).' });
      return;
    }

    patchRow(sub.id, { phase: 'uploading', pct: 0, msg: '' });

    const today = new Date().toISOString().slice(0, 10);
    const fields = { lenderName: lenderName || '', docDate: today };

    try {
      const sessionRes = await authFetch('/.netlify/functions/get-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driveFolderId,
          docTypeKey: TERM_SHEET_DOC_TYPE,
          fields,
          categoryOverride: null,
          fileExtension: ext,
          fileSize: file.size,
          originalFilename: file.name,
          dealName: deal?.fields?.['Deal name'] || '',
        }),
      });
      const sessionData = await sessionRes.json();
      if (!sessionRes.ok) throw new Error(sessionData.error || 'Failed to start upload');
      const { sessionUri, mimeType } = sessionData;

      const driveFile = await uploadToDriveAndGetFileMetadata({
        sessionUri, file, mimeType,
        onProgress: (pct) => patchRow(sub.id, { phase: 'uploading', pct }),
      });
      if (!driveFile || !driveFile.id) throw new Error('Drive did not return a file ID.');

      const confirmRes = await authFetch('/.netlify/functions/confirm-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driveFileId: driveFile.id,
          dealId,
          contactId: null,
          docTypeKey: TERM_SHEET_DOC_TYPE,
          fields,
          categoryOverride: null,
          submissionId: sub.id,
        }),
      });
      const confirmData = await confirmRes.json();
      if (!confirmRes.ok) throw new Error(confirmData.error || 'Failed to record upload');

      // Auto-run AI extraction on the new term sheet document. Extraction now
      // runs in the background, so trigger it and poll until it settles.
      patchRow(sub.id, { phase: 'reading', msg: 'Reading term sheet' });
      if (confirmData.documentId) {
        try {
          await authFetch('/.netlify/functions/extract-document', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ documentId: confirmData.documentId }),
          });
          await pollExtractionStatus(confirmData.documentId);
        } catch (e) {
          // Non-fatal: the term sheet is uploaded and linked; AI can be re-run
          // from Documents if this hiccups.
          console.error('term sheet extraction failed (non-fatal):', e);
        }
      }

      clearRow(sub.id);
      await refresh();
    } catch (e) {
      patchRow(sub.id, { phase: 'error', msg: e.message });
    }
  }, [driveFolderId, dealId, refresh]);

  const pickAndUpload = (sub, lenderName) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_EXTENSIONS.map((e) => '.' + e).join(',');
    input.onchange = (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) uploadTermSheet(sub, file, lenderName);
    };
    input.click();
  };

  const termSheetCount = summary.termSheets || 0;
  const canCompare = termSheetCount >= 2;

  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <i className="ti ti-send" style={{ color: '#154D97', fontSize: '14px' }} />
        <div style={{ ...s.cardTitle, flex: 1 }}>SUBMISSIONS</div>
        <button style={s.collapseBtn} onClick={() => onToggle && onToggle('submissions')}>
          <i className={`ti ${collapsed ? 'ti-chevron-right' : 'ti-chevron-down'}`} />
        </button>
      </div>

      {!collapsed && (
        <div style={s.cardBody}>
          <div style={s.toolbar}>
            <div style={s.toolbarHint}>Every lender this deal was sent to, and where each one stands.</div>
            <button style={s.refreshBtn} onClick={refresh} disabled={loading}>
              <i className={`ti ${loading ? 'ti-loader-2' : 'ti-refresh'}`} style={{ fontSize: '13px' }} />
              {loading ? 'Loading' : 'Refresh'}
            </button>
          </div>

          <div style={s.strip}>
            <StripTile k="Sent to" v={summary.sent} />
            <StripTile k="Term sheets in" v={summary.termSheets} tone="good" />
            <StripTile k="Waiting" v={summary.waiting} tone="warn" />
            <StripTile k="Declined" v={summary.declined} tone="dead" />
          </div>

          {error && <div style={s.err}>{error}</div>}
          {loading && submissions.length === 0 && <div style={s.empty}>Loading submissions...</div>}
          {!loading && submissions.length === 0 && !error && (
            <div style={s.empty}>
              No lenders sent yet. Send a lender email from the Lender match tab and it will appear here.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
            {submissions.map((sub) => {
              const lender = sub.lenderId ? lenderById[sub.lenderId] : null;
              const lenderName = (lender && lender.name) || sub.submissionName || 'Lender';
              return (
                <SubmissionRow
                  key={sub.id}
                  sub={sub}
                  lender={lender}
                  lenderName={lenderName}
                  ui={rowState[sub.id]}
                  onUpload={() => pickAndUpload(sub, lenderName)}
                  onViewTerms={() => setViewing({ sub, lenderName, mode: 'readonly' })}
                  onReview={() => setViewing({ sub, lenderName, mode: 'edit' })}
                  onWorksheet={() => setWorksheetFor({ sub, lenderName })}
                />
              );
            })}
          </div>

          {submissions.length > 0 && (
            <div style={canCompare ? s.compareBarOn : s.compareBarOff}>
              <div style={s.compareText}>
                {termSheetCount === 0 && 'Waiting on term sheets to compare.'}
                {termSheetCount === 1 && '1 term sheet in. A second lets you compare side by side.'}
                {termSheetCount >= 2 && `${termSheetCount} term sheets ready to compare.`}
              </div>
              <button
                style={canCompare ? s.compareBtnOn : s.compareBtnOff}
                disabled={!canCompare}
                onClick={() => canCompare && setComparing(true)}
              >
                <i className="ti ti-layout-columns" style={{ fontSize: '13px' }} /> Compare and send to borrower
              </button>
            </div>
          )}
        </div>
      )}

      {viewing && (
        <ViewTermsModal
          submission={viewing.sub}
          lenderName={viewing.lenderName}
          initialMode={viewing.mode || 'readonly'}
          onClose={() => setViewing(null)}
        />
      )}

      {comparing && (
        <CompareModal
          submissions={submissions}
          lenderById={lenderById}
          onClose={() => setComparing(false)}
        />
      )}

      {worksheetFor && (
        <TermWorksheet
          submission={worksheetFor.sub}
          lenderName={worksheetFor.lenderName}
          onClose={() => setWorksheetFor(null)}
        />
      )}
    </div>
  );
}

function StripTile({ k, v, tone }) {
  const vStyle = { ...s.stripV };
  if (tone === 'warn') vStyle.color = '#8A5A12';
  if (tone === 'good') vStyle.color = '#3B6D11';
  if (tone === 'dead') vStyle.color = '#A23A2E';
  return (
    <div style={s.stripTile}>
      <div style={s.stripK}>{k}</div>
      <div style={vStyle}>{v}</div>
    </div>
  );
}

function SubmissionRow({ sub, lender, lenderName, ui, onUpload, onViewTerms, onReview, onWorksheet }) {
  const aeName = lender && lender.aeName ? lender.aeName : '';
  const received = sub.termSheetReceived || sub.status === 'Term sheet received';
  const declined = sub.status === 'Declined';
  const busy = ui && (ui.phase === 'uploading' || ui.phase === 'reading');

  const rowStyle = received ? s.rowOn : (declined ? s.rowDead : s.row);

  const hasTerms = received && (
    sub.proposedRate != null || sub.proposedPoints != null || sub.proposedTerm != null
  );

  return (
    <div style={rowStyle}>
      <div style={s.rowTop}>
        <div style={s.lenderName}>{lenderName}</div>
        <StatusPill received={received} declined={declined} status={sub.status} busy={busy} uiPhase={ui && ui.phase} />
      </div>

      <div style={s.rowMeta}>
        {aeName ? `${aeName} · ` : ''}
        {sub.dateSent ? `Sent ${sub.dateSent}` : 'Sent'}
        {received && sub.responseDate ? ` · received ${sub.responseDate}` : ''}
        {declined && sub.declineReason ? ` · declined: ${sub.declineReason}` : ''}
      </div>

      {busy && (
        <div style={s.progressWrap}>
          <div style={s.progressText}>
            {ui.phase === 'uploading' ? `Uploading ${ui.pct != null ? ui.pct + '%' : ''}` : 'Reading term sheet with AI...'}
          </div>
          <div style={s.progressBar}>
            <div style={{ ...s.progressFill, width: ui.phase === 'reading' ? '100%' : `${ui.pct || 0}%` }} />
          </div>
        </div>
      )}

      {ui && ui.phase === 'error' && <div style={s.rowErr}>Could not upload: {ui.msg}</div>}

      {hasTerms && (
        <div style={s.termGrid}>
          <Term k="Rate" v={sub.proposedRate != null ? `${sub.proposedRate}%` : '-'} />
          <Term k="Points" v={sub.proposedPoints != null ? String(sub.proposedPoints) : '-'} />
          <Term k="Term" v={sub.proposedTerm != null ? `${sub.proposedTerm} mo` : '-'} />
        </div>
      )}

      <div style={s.rowActions}>
        {!received && !declined && !busy && (
          <button style={s.primaryRowBtn} onClick={onUpload}>
            <i className="ti ti-upload" style={{ fontSize: '12px' }} /> Upload term sheet
          </button>
        )}
        {received && (
          <>
            <button style={s.smallBtn} onClick={onReview}><i className="ti ti-columns" style={{ fontSize: '12px' }} /> Review vs original</button>
            <button style={s.smallBtn} onClick={onViewTerms}><i className="ti ti-file-text" style={{ fontSize: '12px' }} /> View terms</button>
            <button style={s.smallBtn} onClick={onWorksheet}><i className="ti ti-calculator" style={{ fontSize: '12px' }} /> Cash to close</button>
            <button style={s.smallBtn} onClick={onUpload}><i className="ti ti-refresh" style={{ fontSize: '12px' }} /> Replace</button>
          </>
        )}
      </div>
    </div>
  );
}

function Term({ k, v }) {
  return (
    <div>
      <div style={s.termK}>{k}</div>
      <div style={s.termV}>{v}</div>
    </div>
  );
}

function StatusPill({ received, declined, status, busy, uiPhase }) {
  if (busy) {
    return <span style={s.pillInfo}>{uiPhase === 'reading' ? 'Reading term sheet' : 'Uploading'}</span>;
  }
  if (received) return <span style={s.pillGood}>Term sheet received</span>;
  if (declined) return <span style={s.pillDead}>Declined</span>;
  if (status === 'Followed up' || status === 'Followed up / chasing') return <span style={s.pillWait}>Followed up</span>;
  if (status === 'Expired') return <span style={s.pillDead}>Expired</span>;
  return <span style={s.pillWait}>Waiting</span>;
}

function ViewTermsModal({ submission, lenderName, initialMode, onClose }) {
  const [mode, setMode] = useState(initialMode === 'edit' ? 'edit' : 'readonly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({ rows: {}, oop: {}, extraOop: [] });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let alive = true;
    const docId = submission.termSheetDocId;
    if (!docId) {
      setError('No term sheet document is linked to this lender yet.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const [exRes, ovRes] = await Promise.all([
          authFetch(`/.netlify/functions/get-document-extraction?documentId=${docId}`),
          authFetch(`/.netlify/functions/term-summary?submissionId=${submission.id}`).catch(() => null),
        ]);
        const d = await exRes.json();
        if (!exRes.ok) throw new Error(d.error || 'Failed to load term sheet');
        if (alive) setData(d);
        if (ovRes && ovRes.ok) {
          const ovJson = await ovRes.json().catch(() => null);
          if (alive && ovJson && ovJson.data && typeof ovJson.data === 'object') {
            setOverrides(ovJson.data);
          }
        }
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [submission]);

  const saveOverrides = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const res = await authFetch('/.netlify/functions/term-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionId: submission.id, data: overrides }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Failed to save');
      setMode('readonly');
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const ex = data && data.extracted ? data.extracted : null;
  const meta = { lenderName, propertyAddress: ex && ex.propertyAddress ? ex.propertyAddress : '' };
  const previewUrl = data ? drivePreviewUrl(data.driveLink) : '';
  const editing = mode === 'edit';

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={{ ...m.modal, maxWidth: editing ? '1000px' : '560px' }} onClick={(e) => e.stopPropagation()}>
        <div style={m.head}>
          <div>
            <div style={m.title}>{lenderName}</div>
            <div style={m.sub}>{editing ? 'Review vs original' : 'Loan term summary'}</div>
          </div>
          <button style={m.closeBtn} onClick={onClose}><i className="ti ti-x" /></button>
        </div>

        <div style={{ ...m.body, padding: editing ? '14px' : '18px' }}>
          {loading && <div style={s.empty}>Loading term sheet...</div>}
          {error && <div style={s.err}>{error}</div>}

          {!loading && !error && data && ex && (
            editing ? (
              <div style={vt.twoCol}>
                <div style={vt.pdfCol}>
                  <div style={vt.pdfLabel}>Lender's original</div>
                  {previewUrl ? (
                    <iframe title="Original term sheet" src={previewUrl} style={vt.frame} />
                  ) : (
                    <div style={vt.noPdf}>
                      Preview not available.
                      {data.driveLink ? <a href={data.driveLink} target="_blank" rel="noopener noreferrer" style={m.openLink}> Open original PDF</a> : null}
                    </div>
                  )}
                </div>
                <div style={vt.sheetCol}>
                  <TermSummarySheet extracted={ex} overrides={overrides} mode="edit" meta={meta} onChange={setOverrides} />
                </div>
              </div>
            ) : (
              <div style={vt.readWrap}>
                <TermSummarySheet extracted={ex} overrides={overrides} mode="readonly" meta={meta} />
              </div>
            )
          )}

          {!loading && !error && data && !ex && (
            <div style={m.note}>
              The AI could not parse a structured term sheet from this document.
              {data.driveLink ? ' Open the original PDF to review it directly.' : ''}
              {data.summary ? ` AI summary: ${data.summary}` : ''}
            </div>
          )}
        </div>

        {!loading && !error && data && ex && (
          <div style={vt.actions}>
            {editing ? (
              <>
                <button style={vt.btnGhost} onClick={onClose} disabled={saving}><i className="ti ti-x" style={{ fontSize: '14px', verticalAlign: '-2px' }} /> Cancel</button>
                {saveError ? <span style={vt.saveErr}>{saveError}</span> : null}
                <button style={vt.btnPrimary} onClick={saveOverrides} disabled={saving}>
                  <i className={`ti ${saving ? 'ti-loader-2' : 'ti-device-floppy'}`} style={{ fontSize: '14px', verticalAlign: '-2px' }} /> {saving ? 'Saving' : 'Save and preview'}
                </button>
              </>
            ) : (
              <>
                <button style={vt.btnGhost} onClick={() => setMode('edit')}><i className="ti ti-edit" style={{ fontSize: '14px', verticalAlign: '-2px' }} /> Back to edit</button>
                <button style={vt.btnGhost} onClick={() => window.print()}><i className="ti ti-download" style={{ fontSize: '14px', verticalAlign: '-2px' }} /> Download PDF</button>
                <button style={vt.btnSend}><i className="ti ti-send" style={{ fontSize: '14px', verticalAlign: '-2px' }} /> Send to borrower</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function drivePreviewUrl(link) {
  if (!link) return '';
  const a = String(link).match(/\/file\/d\/([^/]+)/);
  if (a) return `https://drive.google.com/file/d/${a[1]}/preview`;
  const b = String(link).match(/[?&]id=([^&]+)/);
  if (b) return `https://drive.google.com/file/d/${b[1]}/preview`;
  return '';
}

function CompareModal({ submissions, lenderById, onClose }) {
  const received = (Array.isArray(submissions) ? submissions : []).filter(
    (s) => s.termSheetReceived || s.status === 'Term sheet received'
  );
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const out = await Promise.all(received.map(async (sub) => {
        const lender = sub.lenderId ? lenderById[sub.lenderId] : null;
        const lenderName = (lender && lender.name) || sub.submissionName || 'Lender';
        const docId = sub.termSheetDocId;
        let ex = null;
        let overrides = null;
        let error = '';
        if (!docId) return { id: sub.id, lenderName, ex, overrides, error: 'No term sheet linked' };
        try {
          const [exRes, ovRes] = await Promise.all([
            authFetch(`/.netlify/functions/get-document-extraction?documentId=${docId}`),
            authFetch(`/.netlify/functions/term-summary?submissionId=${sub.id}`).catch(() => null),
          ]);
          const d = await exRes.json();
          if (exRes.ok) ex = d.extracted || null;
          else error = d.error || 'Failed to load';
          if (ovRes && ovRes.ok) {
            const oj = await ovRes.json().catch(() => null);
            if (oj && oj.data && typeof oj.data === 'object') overrides = oj.data;
          }
        } catch (e) {
          error = e.message;
        }
        return { id: sub.id, lenderName, ex, overrides, error };
      }));
      if (alive) { setCards(out); setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div style={m.overlay} onClick={onClose}>
      <div style={cm.modal} onClick={(e) => e.stopPropagation()}>
        <div style={m.head}>
          <div>
            <div style={m.title}>Compare offers</div>
            <div style={m.sub}>{received.length} finalized term {received.length === 1 ? 'sheet' : 'sheets'}, as the borrower will see them</div>
          </div>
          <button style={m.closeBtn} onClick={onClose}><i className="ti ti-x" /></button>
        </div>

        <div style={cm.body}>
          {loading && <div style={s.empty}>Loading offers...</div>}
          {!loading && cards.length === 0 && <div style={s.empty}>No finalized term sheets to compare yet.</div>}
          {!loading && cards.length > 0 && (
            <div style={cm.row}>
              {cards.map((c) => (
                <div key={c.id} style={cm.col}>
                  <div style={cm.colHead}>{c.lenderName}</div>
                  {c.ex ? (
                    <TermSummarySheet extracted={c.ex} overrides={c.overrides} mode="readonly" meta={{ lenderName: c.lenderName }} />
                  ) : (
                    <div style={cm.colErr}>{c.error || 'No structured terms available for this lender.'}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={cm.foot}>
          <div style={cm.footNote}>Next: send this comparison to the borrower with a private link to pick their offer.</div>
          <button style={vt.btnGhost} onClick={onClose}><i className="ti ti-x" style={{ fontSize: '14px', verticalAlign: '-2px' }} /> Close</button>
        </div>
      </div>
    </div>
  );
}

function fileExtension(filename) {
  if (!filename || !filename.includes('.')) return '';
  return filename.split('.').pop().toLowerCase();
}

function uploadToDriveAndGetFileMetadata({ sessionUri, file, mimeType, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sessionUri, true);
    xhr.setRequestHeader('Content-Type', mimeType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (!data.id) { reject(new Error('Drive upload succeeded but no file ID in response.')); return; }
          resolve(data);
        } catch (e) {
          reject(new Error('Drive upload succeeded but response was not valid JSON: ' + e.message));
        }
      } else {
        reject(new Error(`Drive upload failed: ${xhr.status} ${xhr.responseText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during Drive upload'));
    xhr.send(file);
  });
}

const s = {
  card:        { background: '#fff', borderRadius: '10px', border: '0.5px solid #C9D9EE', overflow: 'hidden', marginBottom: '14px' },
  cardHead:    { background: '#D0E0F5', padding: '9px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '7px', borderBottom: '1px solid #BCD2EC' },
  cardTitle:   { color: '#154D97', fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: "'Inter',sans-serif" },
  collapseBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '14px', padding: 0 },
  cardBody:    { padding: '16px' },

  toolbar:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' },
  toolbarHint: { fontSize: '11.5px', color: '#6a7c98', fontStyle: 'italic' },
  refreshBtn:  { display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', background: 'transparent', color: '#154D97', border: '1.5px solid #C9D9EE', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },

  strip:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px', marginBottom: '14px' },
  stripTile:   { background: '#F0F5FB', border: '1px solid #D9E5F4', borderRadius: '11px', padding: '11px 14px' },
  stripK:      { fontSize: '9px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#7189B0' },
  stripV:      { fontSize: '18px', fontWeight: 600, color: '#154D97', marginTop: '3px' },

  row:         { background: '#fff', border: '1px solid #E4EDF8', borderRadius: '8px', padding: '12px' },
  rowOn:       { background: '#fff', border: '1.5px solid #7AADE0', borderRadius: '8px', padding: '12px' },
  rowDead:     { background: '#FBFAF7', border: '1px solid #E0DBC8', borderRadius: '8px', padding: '12px', opacity: 0.8 },
  rowTop:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' },
  lenderName:  { fontSize: '14px', fontWeight: 600, color: '#0D2545' },
  rowMeta:     { fontSize: '12px', color: '#8a9ab5', marginTop: '3px' },

  progressWrap:{ marginTop: '9px' },
  progressText:{ fontSize: '11.5px', color: '#185FA5', marginBottom: '4px' },
  progressBar: { width: '100%', height: '5px', background: '#E4EDF8', borderRadius: '3px', overflow: 'hidden' },
  progressFill:{ height: '100%', background: '#154D97', transition: 'width 0.2s' },
  rowErr:      { marginTop: '8px', fontSize: '11.5px', color: '#c0392b' },

  termGrid:    { background: '#F5F9FE', border: '1px solid #E4EDF8', borderRadius: '6px', padding: '8px 10px', marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '6px' },
  termK:       { fontSize: '10px', color: '#8a9ab5' },
  termV:       { fontSize: '13px', fontWeight: 600, color: '#0D2545' },

  rowActions:  { display: 'flex', gap: '6px', marginTop: '9px', flexWrap: 'wrap' },
  primaryRowBtn:{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', padding: '6px 12px', background: '#154D97', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 },
  smallBtn:    { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '5px 10px', background: 'transparent', color: '#4A7FBF', border: '1.5px solid #C9D9EE', borderRadius: '6px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 },

  pillGood:    { fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#D4EDDA', color: '#155724', whiteSpace: 'nowrap' },
  pillWait:    { fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#FDF0DC', color: '#7A4A08', whiteSpace: 'nowrap' },
  pillDead:    { fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#F8D7DA', color: '#721C24', whiteSpace: 'nowrap' },
  pillInfo:    { fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#EAE9FE', color: '#4A40A0', whiteSpace: 'nowrap' },

  compareBarOn:  { marginTop: '14px', padding: '12px', background: '#E6F1FB', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' },
  compareBarOff: { marginTop: '14px', padding: '12px', background: '#F5F9FE', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' },
  compareText:   { fontSize: '13px', color: '#185FA5' },
  compareBtnOn:  { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, padding: '8px 16px', background: '#154D97', color: '#fff', border: 'none', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit' },
  compareBtnOff: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, padding: '8px 16px', background: '#B8C7DD', color: '#fff', border: 'none', borderRadius: '7px', cursor: 'not-allowed', fontFamily: 'inherit' },

  err:         { fontSize: '12px', color: '#c0392b', marginTop: '6px', marginBottom: '6px' },
  empty:       { fontSize: '13px', color: '#8a9ab5', padding: '12px 0' },
};

const m = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(13,37,69,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', zIndex: 1000, overflowY: 'auto' },
  modal:     { background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '640px', boxShadow: '0 8px 40px rgba(13,53,101,0.25)', overflow: 'hidden' },
  head:      { background: '#154D97', padding: '14px 18px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  title:     { color: '#fff', fontSize: '16px', fontWeight: 700 },
  sub:       { color: '#C9D9EE', fontSize: '12px', marginTop: '2px' },
  closeBtn:  { background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '18px', padding: 0 },
  body:      { padding: '18px', maxHeight: '70vh', overflowY: 'auto' },
  openLink:  { display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12.5px', color: '#154D97', textDecoration: 'none', fontWeight: 600, marginBottom: '14px' },
  note:      { fontSize: '12.5px', color: '#6a7c98', background: '#F5F9FE', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px' },

  gridWrap:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px', marginBottom: '4px' },
  item:      { background: '#F5F9FE', borderRadius: '7px', padding: '9px 11px' },
  itemK:     { fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#4A7FBF' },
  itemV:     { fontSize: '14px', fontWeight: 600, color: '#0D2545', marginTop: '2px' },

  section:     { marginTop: '16px' },
  sectionTitle:{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#3A6BA8', marginBottom: '6px', borderBottom: '1px solid #E4EDF8', paddingBottom: '4px' },
  leverageRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  levPill:     { fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '5px', background: '#D6E8F8', color: '#1A5FA8' },
  verbatim:    { fontSize: '12px', color: '#6a7c98', fontStyle: 'italic', marginTop: '6px' },
  line:        { fontSize: '13px', color: '#0D2545', marginTop: '3px', lineHeight: 1.5 },
  ul:          { margin: '4px 0 0', paddingLeft: '18px' },
  li:          { fontSize: '13px', color: '#0D2545', marginBottom: '4px', lineHeight: 1.45 },
  summary:     { fontSize: '13px', color: '#0D2545', lineHeight: 1.6 },
  flags:       { fontSize: '13px', color: '#8A5A12', background: '#FDF0DC', borderRadius: '6px', padding: '10px 12px', lineHeight: 1.55, whiteSpace: 'pre-wrap' },
};

const vt = {
  readWrap:   { maxWidth: '500px', margin: '0 auto' },
  twoCol:     { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 500px', gap: '16px', alignItems: 'start' },
  pdfCol:     { minWidth: 0 },
  pdfLabel:   { fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#3A6BA8', marginBottom: '6px' },
  frame:      { width: '100%', height: '620px', border: '1px solid #C5D5EA', borderRadius: '8px', background: '#F5F9FE' },
  noPdf:      { fontSize: '12.5px', color: '#6a7c98', background: '#F5F9FE', border: '1px solid #E4EDF8', borderRadius: '8px', padding: '16px' },
  sheetCol:   { minWidth: 0 },
  actions:    { display: 'flex', gap: '10px', justifyContent: 'center', padding: '14px 18px', borderTop: '1px solid #E4EDF8', flexWrap: 'wrap' },
  btnGhost:   { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 600, padding: '8px 16px', background: 'transparent', color: '#154D97', border: '1.5px solid #154D97', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit' },
  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 700, padding: '8px 18px', background: '#154D97', color: '#fff', border: '1.5px solid #154D97', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit' },
  btnSend:    { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 700, padding: '8px 18px', background: '#2E7D32', color: '#fff', border: '1.5px solid #2E7D32', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit' },
  saveErr:    { fontSize: '12px', color: '#c0392b', alignSelf: 'center' },
};

const cm = {
  modal:    { background: '#fff', borderRadius: '12px', width: '96vw', maxWidth: '1280px', boxShadow: '0 8px 40px rgba(13,53,101,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '92vh' },
  body:     { padding: '16px', overflow: 'auto', flex: 1 },
  row:      { display: 'flex', gap: '16px', alignItems: 'flex-start', minWidth: 'min-content' },
  col:      { flex: '0 0 380px', width: '380px' },
  colHead:  { fontSize: '13px', fontWeight: 700, color: '#154D97', marginBottom: '8px', textAlign: 'center' },
  colErr:   { fontSize: '12.5px', color: '#6a7c98', background: '#F5F9FE', border: '1px solid #E4EDF8', borderRadius: '8px', padding: '16px', textAlign: 'center' },
  foot:     { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 18px', borderTop: '1px solid #E4EDF8', flexWrap: 'wrap' },
  footNote: { fontSize: '12px', color: '#6a7c98', fontStyle: 'italic' },
};

// Background extraction support: extract-document returns immediately and the
// real work runs in extract-document-background, which writes its result to
// the Documents record. This polls that record until the AI status settles.
const EXTRACTION_DONE = ['Reviewed - flagged', 'Reviewed - clean', 'Failed', 'Not applicable'];

async function pollExtractionStatus(documentId, { tries = 60, delayMs = 2500 } = {}) {
  for (let i = 0; i < tries; i++) {
    await new Promise((r) => setTimeout(r, delayMs));
    try {
      const res = await authFetch(`/.netlify/functions/get-document-extraction?documentId=${documentId}`);
      const d = await res.json();
      if (res.ok && d && EXTRACTION_DONE.includes(d.aiReviewStatus)) return d;
    } catch {}
  }
  return null;
}
